import { useState } from "react";
import { formatUsd, type DraftOrder, type LatLng, type Order } from "@moomoo/core";
import { ApiError, api } from "../lib/api.js";
import { errorText, fieldText, type Translate } from "../lib/i18n.js";
import type { AppState } from "../lib/store.js";
import { ScreenBar } from "./bits.js";
import { LocationPicker } from "./LocationPicker.js";

/**
 * Checkout. Where first, then who: the pin decides the fee and fills the
 * address, so it leads the form the way it does in every ride app.
 *
 * The idempotency key is generated once per attempt and kept across retries,
 * which is what makes the Place order button safe to press twice on a bad
 * connection: the server recognises the second submit as the same order.
 */

interface Fields {
  name: string;
  phone: string;
  address: string;
  note: string;
  poNumber: string;
}

const FIELD_LABEL: Record<string, keyof Fields> = {
  customerName: "name",
  phone: "phone",
  address: "address",
  note: "note",
  poNumber: "poNumber",
};

function newKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // older WebViews: good enough for a dedupe key, never used as a secret
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-4000-8000-${Math.random()
    .toString(16)
    .slice(2, 14)}`.replace(/[^0-9a-f-]/g, "0");
}

export function Checkout({
  state,
  t,
  onBack,
  onPlaced,
  onLocation,
  onBadCode,
}: {
  state: AppState;
  t: Translate;
  onBack: () => void;
  onPlaced: (order: Order) => void;
  onLocation: (pin: LatLng | undefined) => void;
  /** The server rejected the business code: forget it and fall back to retail. */
  onBadCode: () => void;
}) {
  const [fields, setFields] = useState<Fields>({
    name: "",
    phone: "",
    address: "",
    note: "",
    poNumber: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [problem, setProblem] = useState<string | undefined>();
  const [sending, setSending] = useState(false);
  // stable for the life of this checkout, so a retry is the same order
  const [idempotencyKey] = useState(newKey);

  const set = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFields((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  async function submit() {
    setProblem(undefined);
    setSending(true);
    const draft = {
      channel: state.channel,
      customerName: fields.name.trim(),
      phone: fields.phone.trim(),
      address: fields.address.trim(),
      ...(state.location ? { location: state.location } : {}),
      ...(state.channel === "wholesale" && state.wholesaleCode
        ? { wholesaleCode: state.wholesaleCode }
        : {}),
      ...(fields.note.trim() ? { note: fields.note.trim() } : {}),
      ...(state.channel === "wholesale" && fields.poNumber.trim()
        ? { poNumber: fields.poNumber.trim() }
        : {}),
      lines: state.totals.lines.map((l) => ({ code: l.product.code, qty: l.qty })),
      idempotencyKey,
    } as DraftOrder;

    try {
      const { order } = await api.placeOrder(draft);
      onPlaced(order);
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.fields?.length) {
          const next: Partial<Record<keyof Fields, string>> = {};
          let general: string | undefined;
          for (const issue of caught.fields) {
            const key = FIELD_LABEL[issue.path];
            const text = fieldText(t, issue.path) ?? t("errGeneric");
            if (key) next[key] = text;
            else general ??= text;
          }
          setErrors(next);
          setProblem(Object.keys(next).length ? general : general ?? t("errGeneric"));
        } else {
          setProblem(errorText(t, caught));
          if (caught.code === "bad_wholesale_code" || caught.code === "wholesale_closed") onBadCode();
        }
      } else {
        setProblem(t("errGeneric"));
      }
    } finally {
      setSending(false);
    }
  }

  const field = (
    key: keyof Fields,
    label: string,
    kind: "text" | "tel" | "area" = "text",
    placeholder?: string,
    autoComplete?: string,
  ) => (
    <>
      <label htmlFor={`f-${key}`}>{label}</label>
      {kind === "area" ? (
        <textarea
          id={`f-${key}`}
          value={fields[key]}
          onChange={set(key)}
          aria-invalid={Boolean(errors[key])}
          {...(autoComplete ? { autoComplete } : {})}
          {...(placeholder ? { placeholder } : {})}
        />
      ) : (
        <input
          id={`f-${key}`}
          type={kind}
          value={fields[key]}
          onChange={set(key)}
          aria-invalid={Boolean(errors[key])}
          {...(kind === "tel" ? { inputMode: "tel" as const } : {})}
          {...(autoComplete ? { autoComplete } : {})}
          {...(placeholder ? { placeholder } : {})}
        />
      )}
      {errors[key] ? (
        <div className="field-error" role="alert">
          {errors[key]}
        </div>
      ) : null}
    </>
  );

  const needsPin = state.channel === "retail" && (!state.location || !state.quote);

  return (
    <>
      <ScreenBar title={t("checkout")} onBack={onBack} backLabel={t("back")} />
      <div className="pad tall">
        {problem ? (
          <div className="banner bad" role="alert" style={{ margin: "8px 0 0" }}>
            {problem}
          </div>
        ) : null}

        {state.channel === "retail" ? (
          <LocationPicker
            value={state.location}
            quote={state.quote}
            quoteError={state.quoteError}
            quoting={state.quoting}
            t={t}
            onChange={(pin, label) => {
              onLocation(pin);
              // the street under the pin is a good start for the address box
              if (label && !fields.address.trim()) setFields((f) => ({ ...f, address: label }));
            }}
          />
        ) : null}

        {field("address", t("address"), "area", undefined, "street-address")}
        {field("name", t("yourName"), "text", undefined, "name")}
        {field("phone", t("phone"), "tel", "012 345 678", "tel")}
        {field("note", t("driverNote"), "text", t("notePlaceholder"), "off")}
        {state.channel === "wholesale" ? field("poNumber", t("poNumber"), "text", t("poPlaceholder"), "off") : null}

        <div className="summary">
          <div className="tot">
            <span>{t("subtotal")}</span>
            <span>{formatUsd(state.totals.subtotal)}</span>
          </div>
          <div className="tot">
            <span>
              {t("delivery")}
              {state.quote && state.channel === "retail" ? ` · ${state.quote.km} km` : ""}
            </span>
            <span>{state.totals.delivery === 0 ? t("free") : formatUsd(state.totals.delivery)}</span>
          </div>
          <div className="tot grand">
            <span>{t("total")}</span>
            <span>{formatUsd(state.totals.total)}</span>
          </div>
          {state.channel === "retail" ? <p className="cashline">{t("payCashLine")}</p> : null}
        </div>
        <p className="note">{t("priceNote")}</p>

        <div className="grow" />
        <div className="actionbar">
          <div className="sum">
            {needsPin ? <small className="need">{t("needPin")}</small> : <small>{t("total")}</small>}
            <b>{formatUsd(state.totals.total)}</b>
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={sending || needsPin || Boolean(state.quoteError)}
          >
            {sending ? t("sending") : t("placeOrder")}
          </button>
        </div>
      </div>
    </>
  );
}
